/**
 * Areas and distances — the pure part of the buyer's area (P25009-425), shared
 * by the browser (util/buyerArea.js, the search, the cards) and the server
 * (bevakning distance alerts, the listing-position writers and backfills).
 *
 * Privacy (BR-6, AC-4): an AREA is a position rounded to two decimals — about
 * 1.1 km north–south and 0.6 km east–west in Sweden. That is what the buyer's
 * position is cut to before it is kept or sent, and what a private seller's
 * listing position is cut to before it is stored.
 *
 * CommonJS, so the server can require it.
 */

/* Two decimals: 1.1 km north–south, about 0.6 km east–west in Sweden. */
const AREA_DECIMALS = 2;

/** The distance chips, in km (BR-2). No radius = "Hela Sverige". */
const RADIUS_OPTIONS_KM = [25, 50, 100];

/* ---- positions ---- */

const round = value => Number(Number(value).toFixed(AREA_DECIMALS));

/** {lat,lng} (or a LatLng) rounded to an area, or null when not a position. */
const coarseArea = position => {
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);
  if (position == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat: round(lat), lng: round(lng) };
};

/** True when a position is already no more precise than an area. */
const isCoarse = position => {
  const coarse = coarseArea(position);
  return !!coarse && coarse.lat === Number(position.lat) && coarse.lng === Number(position.lng);
};

/** An area as the `origin` URL value: "57.71,11.97". */
const areaToOriginParam = area => {
  const coarse = coarseArea(area);
  return coarse ? `${coarse.lat.toFixed(AREA_DECIMALS)},${coarse.lng.toFixed(AREA_DECIMALS)}` : null;
};

/** "57.71,11.97" or a LatLng -> {lat,lng}, or null. */
const parseOriginParam = value => {
  if (value == null || value === '') return null;
  const [lat, lng] =
    typeof value === 'object' ? [value.lat, value.lng] : String(value).split(',');
  const position = { lat: Number(lat), lng: Number(lng) };
  return coarseArea(position) ? position : null;
};

/* ---- distance ---- */

const EARTH_RADIUS_KM = 6371;

/** Great-circle distance in km between {lat,lng} points, or null. */
const distanceKm = (from, to) => {
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat);
  const lng2 = Number(to?.lng);
  if (from == null || to == null || ![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const R = EARTH_RADIUS_KM;
  const rad = deg => (deg * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/** "6 km", "48 km" — kilometres within the chips' range, so a card under
 * "Inom 50 km" reads in the same unit — and the Swedish mil beyond it ("40
 * mil"), how drivers talk about distances at that scale. */
const formatDistance = km => {
  if (!Number.isFinite(km)) return null;
  if (km < 100) return `${Math.max(1, Math.round(km))} km`;
  return `${Math.round(km / 10)} mil`;
};

/* ---- radius (BR-2) ---- */

/** The `radius` URL value as one of the chips' km, or null ("Hela Sverige"). */
const parseRadiusKm = value => {
  const km = Number(value);
  return RADIUS_OPTIONS_KM.includes(km) ? km : null;
};

/* On the same sphere distanceKm measures on, so the box's edges are exactly
 * the radius away by that measure. */
const KM_PER_DEGREE_LAT = (Math.PI * EARTH_RADIUS_KM) / 180;

/**
 * The box around a circle as plain corners { ne: {lat,lng}, sw: {lat,lng} },
 * or null. The east–west half-width is taken at the box's poleward edge, where
 * a degree of longitude is shortest, so no part of the circle falls outside it.
 */
const boxAroundKm = (center, radiusKm) => {
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  if (center == null || !Number.isFinite(lat) || !Number.isFinite(lng) || !(radiusKm > 0)) {
    return null;
  }
  const dLat = radiusKm / KM_PER_DEGREE_LAT;
  const polewardLat = Math.min(89, Math.abs(lat) + dLat);
  const dLng = radiusKm / (KM_PER_DEGREE_LAT * Math.cos((polewardLat * Math.PI) / 180));
  return {
    ne: { lat: Math.min(90, lat + dLat), lng: Math.min(180, lng + dLng) },
    sw: { lat: Math.max(-90, lat - dLat), lng: Math.max(-180, lng - dLng) },
  };
};

/**
 * The position a listing is stored with (P25009-425 AC-4). A listing's
 * position is public data. A business seller's is kept as given: it is their
 * shop, where buyers collect. Anyone else's is rounded to an area, so a private
 * seller's home is never in it. Distances and the chips work the same either
 * way. `null` (remove the position) and `undefined` (leave it) pass through.
 *
 * "business" only, as VAT and the "Återförsäljare" label read it; every other
 * userType is treated as a private person.
 */
const listingPositionFor = (position, userType) => {
  if (position == null) return position;
  if (userType === 'business') return position;
  return coarseArea(position);
};

/** True when the listing's position is within the radius of the area. A
 * listing with no position is never "within": it cannot be placed. */
const isWithinRadius = (center, geolocation, radiusKm) => {
  const km = distanceKm(center, geolocation);
  return km !== null && km <= radiusKm;
};

module.exports = {
  AREA_DECIMALS,
  RADIUS_OPTIONS_KM,
  coarseArea,
  isCoarse,
  areaToOriginParam,
  parseOriginParam,
  distanceKm,
  formatDistance,
  parseRadiusKm,
  boxAroundKm,
  listingPositionFor,
  isWithinRadius,
};
